import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn, formatCurrency } from "@/lib/utils";
import { X, Minus, Plus, ArrowLeftRight } from "lucide-react";
import type { OpenOrder } from "./open-orders";

// Maximum characters allowed in price inputs
const MAX_PRICE_LENGTH = 12;

type EditOrderModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: OpenOrder | null;
  currentMarketPrice: number;
  onConfirm: (orderId: string, newQuantity: number, newPrice: number, takeProfit: number | null, stopLoss: number | null) => Promise<void>;
};

// Price input with +/- buttons
const PriceInput = ({
  value,
  onChange,
  onClear,
  onIncrement,
  onDecrement,
  placeholder,
  suffix,
}: {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  placeholder: string;
  suffix: string;
}) => (
  <div
    className={cn(
      "flex items-center rounded-lg border bg-muted/30 transition-all duration-200 overflow-hidden",
      "border-border hover:border-muted-foreground/60",
      "focus-within:border-foreground/70 dark:focus-within:border-white/80"
    )}
  >
    <input
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const cleaned = e.target.value.replace(/[^0-9.]/g, "");
        if (cleaned.length <= MAX_PRICE_LENGTH) onChange(cleaned);
      }}
      className={cn(
        "flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-muted-foreground/50",
        "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      )}
    />
    {value && (
      <button
        type="button"
        onClick={onClear}
        className="px-1.5 text-muted-foreground/50 hover:text-foreground transition-colors flex-shrink-0"
      >
        <X className="size-3.5" />
      </button>
    )}
    <span className="pr-2 text-sm text-muted-foreground/60 flex-shrink-0">{suffix}</span>
    <div className="flex items-center border-l border-border">
      <button
        type="button"
        onClick={onDecrement}
        className="px-2.5 py-2.5 text-muted-foreground hover:text-foreground transition-colors"
      >
        <Minus className="size-3.5" />
      </button>
      <div className="w-px h-4 bg-border" />
      <button
        type="button"
        onClick={onIncrement}
        className="px-2.5 py-2.5 text-muted-foreground hover:text-foreground transition-colors"
      >
        <Plus className="size-3.5" />
      </button>
    </div>
  </div>
);

export const EditOrderModal = ({
  open,
  onOpenChange,
  order,
  currentMarketPrice,
  onConfirm,
}: EditOrderModalProps) => {
  const [quantity, setQuantity] = useState("");
  const [valueCurrency, setValueCurrency] = useState<"quote" | "base">("base"); // USD or BTC
  const [limitPrice, setLimitPrice] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [stopLoss, setStopLoss] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form values when order changes or dialog opens
  useEffect(() => {
    if (open && order) {
      // Open modal in the same mode the user used to place the order:
      // - If order has notionalValue (placed in USD) → show USD value
      // - If order has no notionalValue (placed in BTC) → show BTC quantity
      if (order.notionalValue) {
        setValueCurrency("quote"); // USD mode
        setQuantity(order.notionalValue.toString()); // Original USD value
      } else {
        setValueCurrency("base"); // BTC mode
        setQuantity(order.quantity.toString()); // BTC quantity
      }
      // Format limit price to 2 decimals for cleaner display
      setLimitPrice(order.limitPrice?.toFixed(2) || "");
      // Initialize TP/SL from order (format to 2 decimals)
      setTakeProfit(order.takeProfit?.toFixed(2) || "");
      setStopLoss(order.stopLoss?.toFixed(2) || "");
      setError(null);
    }
  }, [open, order]);

  // Validate price crossing
  const validatePriceNotCrossed = useCallback(
    (price: string) => {
      if (!order || !price) return null;
      
      const priceNum = parseFloat(price);
      if (isNaN(priceNum) || priceNum <= 0) return null;

      const isLong = order.side === "long" || order.side === "buy";

      if (isLong && priceNum > currentMarketPrice) {
        return `Price $${formatCurrency(priceNum, 2)} is above market ($${formatCurrency(currentMarketPrice, 2)}).`;
      }
      if (!isLong && priceNum < currentMarketPrice) {
        return `Price $${formatCurrency(priceNum, 2)} is below market ($${formatCurrency(currentMarketPrice, 2)}).`;
      }
      return null;
    },
    [order, currentMarketPrice]
  );

  // Handle currency toggle between BTC and USD
  // Uses the LIMIT PRICE for conversion since this is a limit order
  const handleCurrencyToggle = useCallback(() => {
    const qtyNum = parseFloat(quantity);
    const priceNum = parseFloat(limitPrice);
    
    if (isNaN(qtyNum) || isNaN(priceNum) || priceNum === 0) {
      setValueCurrency(valueCurrency === "quote" ? "base" : "quote");
      return;
    }
    
    if (valueCurrency === "base") {
      // Converting quantity (BTC) → value (USD)
      // Use original notionalValue if quantity hasn't changed, otherwise calculate
      const originalQty = order?.quantity || 0;
      const isUnchanged = Math.abs(qtyNum - originalQty) < 0.0000001;
      
      if (isUnchanged && order?.notionalValue) {
        // User hasn't modified quantity, use original USD value
        setQuantity(order.notionalValue.toString());
      } else {
        // Quantity was modified, calculate new USD value
        const usdValue = qtyNum * priceNum;
        setQuantity(usdValue.toFixed(2));
      }
      setValueCurrency("quote");
    } else {
      // Converting value (USD) → quantity (BTC) using LIMIT price
      // User enters desired USD value, we calculate the quantity needed
      const btcValue = qtyNum / priceNum;
      // Use higher precision to avoid losing value during conversion
      const precision = Math.max(order?.base.precision || 4, 8);
      setQuantity(btcValue.toFixed(precision));
      setValueCurrency("base");
    }
  }, [quantity, limitPrice, valueCurrency, order]);

  // Validate all inputs
  const validateInputs = useCallback(() => {
    if (!order) return null;

    const quantityNum = parseFloat(quantity);
    const priceNum = parseFloat(limitPrice);

    if (!quantity || quantityNum <= 0) {
      return "Quantity must be greater than 0";
    }

    if (!limitPrice || priceNum <= 0) {
      return "Limit price must be greater than 0";
    }

    // Check price crossing
    const priceCrossError = validatePriceNotCrossed(limitPrice);
    if (priceCrossError) {
      return priceCrossError;
    }

    // Check if values changed (including TP/SL)
    const originalTp = order.takeProfit?.toFixed(2) || "";
    const originalSl = order.stopLoss?.toFixed(2) || "";
    const tpChanged = takeProfit !== originalTp;
    const slChanged = stopLoss !== originalSl;
    
    if (
      quantityNum === order.quantity &&
      priceNum === (order.limitPrice || 0) &&
      !tpChanged &&
      !slChanged
    ) {
      return "No changes made";
    }

    return null;
  }, [order, quantity, limitPrice, takeProfit, stopLoss, validatePriceNotCrossed]);

  // Update error message in real-time
  useEffect(() => {
    const validationError = validateInputs();
    setError(validationError);
  }, [quantity, limitPrice, takeProfit, stopLoss, validateInputs]);

  // Handle +/- buttons for quantity
  const incrementQuantity = () => {
    const current = parseFloat(quantity) || 0;
    const step = valueCurrency === "quote"
      ? (current > 10000 ? 1000 : current > 1000 ? 100 : 10)
      : (current > 100 ? 10 : current > 10 ? 1 : 0.1);
    const newValue = current + step;
    const decimals = valueCurrency === "quote" ? 2 : (order?.base.precision || 4);
    setQuantity(newValue.toFixed(decimals));
  };

  const decrementQuantity = () => {
    const current = parseFloat(quantity) || 0;
    const step = valueCurrency === "quote"
      ? (current > 10000 ? 1000 : current > 1000 ? 100 : 10)
      : (current > 100 ? 10 : current > 10 ? 1 : 0.1);
    const newValue = Math.max(0, current - step);
    const decimals = valueCurrency === "quote" ? 2 : (order?.base.precision || 4);
    setQuantity(newValue.toFixed(decimals));
  };

  // Handle +/- buttons for price
  const incrementPrice = () => {
    const current = parseFloat(limitPrice) || 0;
    const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
    const newValue = current + step;
    setLimitPrice(newValue.toFixed(2));
  };

  const decrementPrice = () => {
    const current = parseFloat(limitPrice) || 0;
    const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
    const newValue = Math.max(0, current - step);
    setLimitPrice(newValue.toFixed(2));
  };

  // Handle +/- buttons for Take Profit
  const incrementTakeProfit = () => {
    const current = parseFloat(takeProfit) || currentMarketPrice;
    const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
    const newValue = current + step;
    setTakeProfit(newValue.toFixed(2));
  };

  const decrementTakeProfit = () => {
    const current = parseFloat(takeProfit) || currentMarketPrice;
    const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
    const newValue = Math.max(0, current - step);
    setTakeProfit(newValue.toFixed(2));
  };

  // Handle +/- buttons for Stop Loss
  const incrementStopLoss = () => {
    const current = parseFloat(stopLoss) || currentMarketPrice;
    const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
    const newValue = current + step;
    setStopLoss(newValue.toFixed(2));
  };

  const decrementStopLoss = () => {
    const current = parseFloat(stopLoss) || currentMarketPrice;
    const step = current > 1000 ? 10 : current > 100 ? 1 : 0.1;
    const newValue = Math.max(0, current - step);
    setStopLoss(newValue.toFixed(2));
  };

  // Calculate total value (always uses LIMIT price for limit orders)
  // This shows what the order will be worth when it fills at the limit price
  const totalValue = useCallback(() => {
    const priceNum = parseFloat(limitPrice);
    
    if (valueCurrency === "quote") {
      // When in USD mode, quantity IS the total value (user entered USD directly)
      const qtyNum = parseFloat(quantity);
      return isNaN(qtyNum) ? 0 : qtyNum;
    } else {
      // When in BTC mode, calculate value at LIMIT price
      // Total Value = quantity × limit price (the fill price for limit orders)
      const qtyNum = parseFloat(quantity);
      if (isNaN(qtyNum) || isNaN(priceNum)) return 0;
      return qtyNum * priceNum;
    }
  }, [quantity, limitPrice, valueCurrency]);

  // Calculate estimated profit/loss for TP/SL
  const estimatedPnL = useCallback(() => {
    const tpNum = parseFloat(takeProfit) || 0;
    const slNum = parseFloat(stopLoss) || 0;
    const priceNum = parseFloat(limitPrice) || 0;
    
    // Calculate quantity in base asset (BTC)
    const qtyNum = valueCurrency === "quote" 
      ? (parseFloat(quantity) || 0) / priceNum 
      : (parseFloat(quantity) || 0);
    
    if (!order || priceNum <= 0 || qtyNum <= 0) {
      return { estProfit: null, estLoss: null };
    }
    
    const isLongOrder = order.side === "long" || order.side === "buy";
    
    let estProfit: number | null = null;
    let estLoss: number | null = null;
    
    if (isLongOrder) {
      // Long: profit when price goes UP to TP, loss when price goes DOWN to SL
      estProfit = tpNum > 0 ? (tpNum - priceNum) * qtyNum : null;
      estLoss = slNum > 0 ? (priceNum - slNum) * qtyNum : null;
    } else {
      // Short: profit when price goes DOWN to TP, loss when price goes UP to SL
      estProfit = tpNum > 0 ? (priceNum - tpNum) * qtyNum : null;
      estLoss = slNum > 0 ? (slNum - priceNum) * qtyNum : null;
    }
    
    return { estProfit, estLoss };
  }, [takeProfit, stopLoss, limitPrice, quantity, valueCurrency, order]);

  const handleSubmit = async () => {
    if (!order) return;

    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let quantityNum = parseFloat(quantity);
      const priceNum = parseFloat(limitPrice);

      // If user entered in USD, convert to BTC
      if (valueCurrency === "quote") {
        quantityNum = quantityNum / priceNum;
      }

      // Parse TP/SL values (empty string = null = no TP/SL)
      const tpValue = takeProfit ? parseFloat(takeProfit) : null;
      const slValue = stopLoss ? parseFloat(stopLoss) : null;

      await onConfirm(order.id, quantityNum, priceNum, tpValue, slValue);
      
      // Modal will be closed by parent component on success
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to modify order";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!order) return null;

  const isLong = order.side === "long" || order.side === "buy";
  const hasError = !!error;
  const canSubmit = !hasError && !isSubmitting && error !== "No changes made";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[520px] gap-0 p-0 overflow-hidden bg-card border-border"
        showCloseButton={false}
      >
        {/* Header */}
        <div className="px-7 pt-7 pb-4">
          <DialogHeader className="gap-0.5">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-semibold">
                Modify Order - {order.symbol}
              </DialogTitle>
              <button
                onClick={() => onOpenChange(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
                disabled={isSubmitting}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              <span
                className={cn(
                  "font-medium",
                  isLong ? "text-bid-foreground" : "text-ask-foreground"
                )}
              >
                {isLong ? "Long" : "Short"}
              </span>{" "}
              • Limit Order
            </p>
          </DialogHeader>
        </div>

        {/* Order Info */}
        <div className="px-7 pb-4">
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Price</span>
              <span className="text-foreground tabular-nums font-medium">
                ${formatCurrency(currentMarketPrice, order.quote.precision)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Quantity</span>
              <span className="text-foreground tabular-nums font-medium">
                {formatCurrency(order.quantity, order.base.precision)} {order.base.code}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Price</span>
              <span className="text-foreground tabular-nums font-medium">
                ${formatCurrency(order.limitPrice || 0, order.quote.precision)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Value</span>
              <span className="text-foreground tabular-nums font-medium">
                ${formatCurrency(order.notionalValue ?? (order.quantity * (order.limitPrice || 0)), 2)}
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-border" />

        {/* Input Section */}
        <div className="px-7 py-6 space-y-6">
          {/* Quantity Input */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">
              {valueCurrency === "quote" ? "Value" : "Quantity"} ({valueCurrency === "quote" ? order.quote.code : order.base.code})
            </label>
            <div className="relative">
              <PriceInput
                value={quantity}
                onChange={setQuantity}
                onClear={() => setQuantity("")}
                onIncrement={incrementQuantity}
                onDecrement={decrementQuantity}
                placeholder="0.00"
                suffix={valueCurrency === "quote" ? order.quote.code : order.base.code}
              />
              <button
                type="button"
                onClick={handleCurrencyToggle}
                className="absolute right-[140px] top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer z-10 px-1"
                title={`Switch to ${valueCurrency === "quote" ? order.base.code : order.quote.code}`}
              >
                <ArrowLeftRight className="size-3" />
              </button>
            </div>
          </div>

          {/* Limit Price Input */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">
              Limit Price ({order.quote.code})
            </label>
            <PriceInput
              value={limitPrice}
              onChange={setLimitPrice}
              onClear={() => setLimitPrice("")}
              onIncrement={incrementPrice}
              onDecrement={decrementPrice}
              placeholder="0.00"
              suffix={order.quote.code}
            />
          </div>

          {/* TP/SL Section */}
          <div className="grid grid-cols-2 gap-4">
            {/* Take Profit Input */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-bid-foreground flex items-center justify-between">
                <span>Take Profit</span>
                {estimatedPnL().estProfit !== null && (
                  <span className="text-bid-foreground tabular-nums">
                    +${formatCurrency(Math.abs(estimatedPnL().estProfit!), 2)}
                  </span>
                )}
              </label>
              <PriceInput
                value={takeProfit}
                onChange={setTakeProfit}
                onClear={() => setTakeProfit("")}
                onIncrement={incrementTakeProfit}
                onDecrement={decrementTakeProfit}
                placeholder="Optional"
                suffix={order.quote.code}
              />
            </div>

            {/* Stop Loss Input */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-ask-foreground flex items-center justify-between">
                <span>Stop Loss</span>
                {estimatedPnL().estLoss !== null && (
                  <span className="text-ask-foreground tabular-nums">
                    -${formatCurrency(Math.abs(estimatedPnL().estLoss!), 2)}
                  </span>
                )}
              </label>
              <PriceInput
                value={stopLoss}
                onChange={setStopLoss}
                onClear={() => setStopLoss("")}
                onIncrement={incrementStopLoss}
                onDecrement={decrementStopLoss}
                placeholder="Optional"
                suffix={order.quote.code}
              />
            </div>
          </div>

          {/* Total Value Display */}
          <div className="p-3 rounded-lg bg-muted/20 border border-border">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total Value</span>
              <span className="text-sm font-medium text-foreground tabular-nums">
                ${formatCurrency(totalValue(), 2)}
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && error !== "No changes made" && (
            <div className="text-xs text-destructive bg-destructive/10 p-3 rounded-lg text-center">
              {error}
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-3 px-7 pb-7 pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-11"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 h-11 bg-buy hover:bg-buy/90 text-white"
            disabled={!canSubmit}
          >
            {isSubmitting ? "Updating order..." : "Confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

